// capture_items table — split out of schema.ts to keep that file within
// the 500-line policy cap (lint:max-lines), following the same pattern as
// schema-templates.ts / schema-assignments.ts. Re-exported from
// @citycare/shared so import paths are unchanged. One-way edge: this file
// imports the tables it references from ./schema; nothing in ./schema
// references capture_items, so there is no import cycle.

import { sql } from 'drizzle-orm'
import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'

import { assetTypes, captureSessions, sites, spaces, users } from './schema'
import { updatedAtColumn } from './schema-shared'

// capture_items — site items target site_id only; building items target
// building_id + space_id. Zero-confirmed rows must still carry quantity 0.
export const captureItems = pgTable(
  'capture_items',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    captureSessionId: uuid('capture_session_id')
      .notNull()
      .references(() => captureSessions.id, { onDelete: 'cascade' }),
    // Denormalised owner (= the parent session's user_id), maintained by the
    // tg_capture_items_set_user_id BEFORE INSERT/UPDATE trigger (migration
    // 0021) so the captures stream scopes items with ONE bucket per user
    // (`WHERE user_id = auth.user_id()`) instead of one bucket + two
    // parameter-query results per session (PSYNC_S2305, prod incident
    // 2026-09-01). App writers pass the session's user_id; the trigger is
    // the authority and overrides any mismatch.
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    siteId: uuid('site_id')
      .notNull()
      .references(() => sites.id, { onDelete: 'restrict' }),
    buildingId: uuid('building_id'),
    spaceId: uuid('space_id'),
    assetTypeId: uuid('asset_type_id')
      .notNull()
      .references(() => assetTypes.id, { onDelete: 'restrict' }),
    quantity: numeric('quantity', { precision: 10, scale: 2 })
      .notNull()
      .default('0'),
    isZeroConfirmed: boolean('is_zero_confirmed').notNull().default(false),
    notes: text('notes'),
    capturedAt: timestamp('captured_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: updatedAtColumn(),
    syncVersion: integer('sync_version').notNull().default(1),
    // Soft-delete marker for admin Reject (both pairing reject and
    // variant reject). Non-null rows are hidden from the review
    // queue, CSV export, dashboards, breakdowns, and progress — but
    // remain in the table so admins can Restore from the Exclusions
    // page (un-set this column + remove the exclusion row).
    rejectedAt: timestamp('rejected_at', { withTimezone: true }),
  },
  (t) => ({
    // Partial unique: one LIVE row per (session, space|site, asset). The
    // `rejected_at IS NULL` predicate excludes admin-rejected (soft-deleted)
    // rows so re-adding a rejected asset on a reopened (in-progress) session
    // INSERTs a fresh live row instead of colliding — a duplicate-key 23505
    // → Encore 500 → the PowerSync connector retries forever → device queue
    // wedge (C1/#689). The rejected tombstone stays hidden via the same
    // `rejected_at IS NULL` filter every server-side read path applies; the
    // offline PowerSync read hooks are filtered to match under #708.
    uniqueSessionSpaceAsset: uniqueIndex(
      'capture_items_session_space_asset_unique',
    )
      .on(t.captureSessionId, t.spaceId, t.assetTypeId)
      .where(sql`${t.spaceId} IS NOT NULL AND ${t.rejectedAt} IS NULL`),
    uniqueSessionSiteAsset: uniqueIndex(
      'capture_items_session_site_asset_unique',
    )
      .on(t.captureSessionId, t.siteId, t.assetTypeId)
      .where(sql`${t.spaceId} IS NULL AND ${t.rejectedAt} IS NULL`),
    sessionIdx: index('capture_items_session_id_idx').on(t.captureSessionId),
    userIdx: index('capture_items_user_id_idx').on(t.userId),
    siteIdx: index('capture_items_site_id_idx').on(t.siteId),
    buildingIdx: index('capture_items_building_id_idx')
      .on(t.buildingId)
      .where(sql`${t.buildingId} IS NOT NULL`),
    spaceIdx: index('capture_items_space_id_idx')
      .on(t.spaceId)
      .where(sql`${t.spaceId} IS NOT NULL`),
    assetTypeIdx: index('capture_items_asset_type_id_idx').on(t.assetTypeId),
    quantityNonNegative: check(
      'capture_items_quantity_non_negative_check',
      sql`${t.quantity} >= 0`,
    ),
    zeroConfirmationCheck: check(
      'capture_items_zero_confirmation_check',
      sql`${t.isZeroConfirmed} = false OR ${t.quantity} = 0`,
    ),
    scopeTargetCheck: check(
      'capture_items_scope_target_check',
      sql`(${t.spaceId} IS NULL AND ${t.buildingId} IS NULL) OR (${t.spaceId} IS NOT NULL AND ${t.buildingId} IS NOT NULL)`,
    ),
    sessionSiteConsistency: foreignKey({
      columns: [t.captureSessionId, t.siteId],
      foreignColumns: [captureSessions.id, captureSessions.siteId],
      name: 'capture_items_session_site_consistency_fk',
    }).onDelete('cascade'),
    sessionBuildingConsistency: foreignKey({
      columns: [t.captureSessionId, t.buildingId],
      foreignColumns: [captureSessions.id, captureSessions.buildingId],
      name: 'capture_items_session_building_consistency_fk',
    }).onDelete('cascade'),
    spaceBuildingConsistency: foreignKey({
      columns: [t.spaceId, t.buildingId],
      foreignColumns: [spaces.id, spaces.buildingId],
      name: 'capture_items_space_building_consistency_fk',
    }).onDelete('restrict'),
  }),
)
